/**
 * ProductTour — a lightweight, dependency-free guided tour that can span pages.
 *
 * Spotlights real elements (matched by CSS selector) and shows a branded tooltip
 * with Back / Next / Skip. A step may carry a `route`; when it activates, the tour
 * navigates there, waits for the target to mount, then spotlights it. It only points
 * at UI — it never clicks anything, so it triggers no API calls / paid lookups.
 *
 * Render this ONCE above the router's <Routes> (e.g., in a provider) so it survives
 * navigation. Optional, skippable, replayable — never blocks the user.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, ArrowRight, ArrowLeft } from 'lucide-react';

export interface TourStep {
  /** CSS selector for the element to spotlight. Omit for a media (sampleUrl) step. */
  selector?: string;
  title: string;
  body: string;
  /** Optional route to navigate to before showing this step (multi-page tour). */
  route?: string;
  /** When set, the step shows this URL (e.g. a sample PDF) in a centered viewer
   *  instead of spotlighting a page element. */
  sampleUrl?: string;
}

interface ProductTourProps {
  steps: TourStep[];
  run: boolean;
  onClose: () => void;
}

const PAD = 8;          // spotlight padding around the target
const CARD_W = 340;     // tooltip width

const ProductTour: React.FC<ProductTourProps> = ({ steps, run, onClose }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // Which step index we've already issued a navigation for — prevents a redirect
  // loop if a page bounces the user (e.g. lacks access), which would otherwise wedge
  // the tour on that step.
  const navForIndex = useRef<number>(-1);

  const step = steps[index];

  // Reset to the first step whenever the tour (re)starts.
  useEffect(() => { if (run) { setIndex(0); navForIndex.current = -1; } }, [run]);

  // Activate the current step: navigate if needed, then poll for the target element.
  useEffect(() => {
    if (!run || !step) return;
    let cancelled = false;

    // Media step (e.g. sample PDF): no element to spotlight — show the centered viewer.
    if (step.sampleUrl || !step.selector) {
      setRect(null);
      return;
    }

    // Navigate first if this step lives on another page.
    if (step.route && location.pathname !== step.route) {
      // Navigate at most once per step. If the destination bounces us elsewhere,
      // we won't keep re-navigating — the step just falls back to a centered tooltip.
      if (navForIndex.current !== index) {
        navForIndex.current = index;
        setRect(null);
        navigate(step.route);
      }
      return; // location change re-runs this effect; the poll happens once we're there.
    }

    // Same page (or just arrived): find the target and place the spotlight once.
    // Keep the previous box until the new target is found (no dark flash), and use an
    // instant 'nearest' scroll — smooth/centered scrolling made the box appear at the
    // pre-scroll (header) position and then animate down, which read as a jump.
    let tries = 0;
    const place = () => {
      if (cancelled) return;
      const el = step.selector ? (document.querySelector(step.selector) as HTMLElement | null) : null;
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
        setRect(el.getBoundingClientRect());
      } else if (tries++ < 40) {
        setTimeout(place, 100); // wait for async-loading pages (up to ~4s)
      } else {
        setRect(null); // give up — tooltip falls back to center, text still shows
      }
    };
    place();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, index, step?.selector, step?.route, location.pathname]);

  // Keep the spotlight pinned to the target during scroll / resize.
  useLayoutEffect(() => {
    if (!run || !step) return;
    const remeasure = () => {
      if (!step.selector) return;
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, step?.selector]);

  if (!run || !step) return null;

  const isLast = index === steps.length - 1;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const sx = rect ? Math.max(PAD, rect.left - PAD) : 0;
  const sy = rect ? Math.max(PAD, rect.top - PAD) : 0;
  const sw = rect ? Math.min(rect.width + PAD * 2, vw - sx - PAD) : 0;
  const sh = rect ? rect.height + PAD * 2 : 0;

  let cardStyle: React.CSSProperties;
  if (rect) {
    const below = sy + sh + 12;
    const placeBelow = below + 180 < vh;
    const top = placeBelow ? below : Math.max(12, sy - 12 - 200);
    let left = sx + sw / 2 - CARD_W / 2;
    left = Math.min(Math.max(12, left), vw - CARD_W - 12);
    cardStyle = { top, left, width: CARD_W };
  } else {
    cardStyle = { top: vh / 2 - 110, left: vw / 2 - CARD_W / 2, width: CARD_W };
  }

  const finish = () => { setIndex(0); onClose(); };

  // Media step: show the sample document in a centered viewer (no spotlight).
  if (step.sampleUrl) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)' }}>
        <div className="bg-white rounded-2xl shadow-2xl border border-black/[0.06] w-full max-w-3xl max-h-[92vh] flex flex-col animate-fade-in-up">
          <div className="p-5 pb-3 relative">
            <button onClick={finish} aria-label="Close tour" className="absolute top-3 right-3 text-[#94a3b8] hover:text-[#475569] transition-colors">
              <X className="w-4 h-4" />
            </button>
            <div className="text-xs font-semibold text-[#c41e3a] uppercase tracking-wider mb-1">Step {index + 1} of {steps.length}</div>
            <h3 className="text-base font-bold text-[#0f172a] mb-1 pr-5">{step.title}</h3>
            <p className="text-sm text-[#475569] leading-relaxed">{step.body}</p>
          </div>
          <div className="px-5 flex-1 min-h-0">
            <iframe title="Sample report" src={step.sampleUrl} className="w-full h-[55vh] rounded-lg border border-black/[0.08] bg-[#f8fafc]" />
          </div>
          <div className="flex items-center justify-between p-5 pt-3">
            <button onClick={finish} className="text-sm text-[#94a3b8] hover:text-[#475569] font-medium transition-colors">Skip</button>
            <div className="flex items-center gap-2">
              {index > 0 && (
                <button onClick={() => setIndex((i) => i - 1)} className="px-3 py-1.5 rounded-lg border border-black/[0.08] text-[#475569] text-sm font-medium hover:bg-gray-50 flex items-center gap-1">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
              )}
              <button onClick={() => (isLast ? finish() : setIndex((i) => i + 1))} className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#c41e3a] to-[#a01830] text-white text-sm font-semibold hover:from-[#a01830] hover:to-[#8a1428] flex items-center gap-1 transition-all">
                {isLast ? 'Done' : (<>Next <ArrowRight className="w-3.5 h-3.5" /></>)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999]">
      {rect ? (
        <div
          className="absolute rounded-xl transition-[top,left,width,height] duration-200 ease-out pointer-events-none"
          style={{ top: sy, left: sx, width: sw, height: sh, boxShadow: '0 0 0 9999px rgba(15,23,42,0.55)' }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,0.55)' }} />
      )}

      <div className="absolute bg-white rounded-2xl shadow-2xl border border-black/[0.06] p-5 animate-fade-in-up" style={cardStyle}>
        <button onClick={finish} aria-label="Close tour" className="absolute top-3 right-3 text-[#94a3b8] hover:text-[#475569] transition-colors">
          <X className="w-4 h-4" />
        </button>
        <div className="text-xs font-semibold text-[#c41e3a] uppercase tracking-wider mb-1">Step {index + 1} of {steps.length}</div>
        <h3 className="text-base font-bold text-[#0f172a] mb-1.5 pr-5">{step.title}</h3>
        <p className="text-sm text-[#475569] leading-relaxed">{step.body}</p>

        <div className="flex items-center justify-between mt-4">
          <button onClick={finish} className="text-sm text-[#94a3b8] hover:text-[#475569] font-medium transition-colors">Skip</button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <button onClick={() => setIndex((i) => i - 1)} className="px-3 py-1.5 rounded-lg border border-black/[0.08] text-[#475569] text-sm font-medium hover:bg-gray-50 flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            <button
              onClick={() => (isLast ? finish() : setIndex((i) => i + 1))}
              className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#c41e3a] to-[#a01830] text-white text-sm font-semibold hover:from-[#a01830] hover:to-[#8a1428] flex items-center gap-1 transition-all"
            >
              {isLast ? 'Done' : (<>Next <ArrowRight className="w-3.5 h-3.5" /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductTour;
